/**
 * Anchor Scroll Handler
 * Smoothly scrolls to section anchors and resolves Shopify Online Store 2.0 dynamic section IDs.
 */
(() => {
  /**
   * Resolves target element from a hash string.
   * @param {string} hash - e.g. '#pegasus_heading' or '#shopify-section-pegasus_heading'
   * @returns {HTMLElement | null}
   */
  function findAnchorTarget(hash) {
    if (!hash || hash === '#') return null;
    const rawId = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!rawId) return null;

    // 1. Direct ID match (e.g. id="pegasus_heading")
    let target = document.getElementById(rawId);
    if (target) return target;

    // 2. If hash starts with 'shopify-section-', strip it and look for section key
    if (rawId.startsWith('shopify-section-')) {
      const sectionKey = rawId.replace('shopify-section-', '');
      target =
        document.querySelector(`[id$="__${CSS.escape(sectionKey)}"]`) ||
        document.getElementById(sectionKey);
      if (target) return target;
    }

    // 3. Match section key ending in OS 2.0 template section ID (e.g. template--...__rawId)
    target = document.querySelector(`[id$="__${CSS.escape(rawId)}"]`);
    if (target) return target;

    // 4. Match elements containing the raw ID or name attribute
    target =
      document.querySelector(`[name="${CSS.escape(rawId)}"]`) ||
      document.querySelector(`[data-section-id="${CSS.escape(rawId)}"]`);
    if (target) return target;

    return null;
  }

  /**
   * Scrolls smoothly to an element taking into account the header height.
   * @param {HTMLElement} target
   * @param {string} [hash]
   */
  function scrollToTarget(target, hash) {
    if (!target) return;

    // Get current header height custom property from body or computed style
    const style = getComputedStyle(document.body);
    const headerHeight = parseFloat(style.getPropertyValue('--header-height')) || 0;
    const headerGroupHeight = parseFloat(style.getPropertyValue('--header-group-height')) || 0;
    const offset = Math.max(headerHeight, headerGroupHeight) + 16;

    const targetRect = target.getBoundingClientRect();
    const targetTop = targetRect.top + window.pageYOffset - offset;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    if (hash && window.history?.pushState) {
      window.history.pushState(null, '', hash);
    }

    // Set focus for accessibility without scrolling
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  }

  // Delegated click listener for in-page anchors
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || !href.includes('#')) return;

    try {
      const url = new URL(link.href, window.location.href);
      // Only handle if it's pointing to the current pathname
      if (url.pathname === window.location.pathname && url.hash) {
        const target = findAnchorTarget(url.hash);
        if (target) {
          event.preventDefault();
          scrollToTarget(target, url.hash);
        }
      }
    } catch {
      // Fallback for relative hash links
      if (href.startsWith('#')) {
        const target = findAnchorTarget(href);
        if (target) {
          event.preventDefault();
          scrollToTarget(target, href);
        }
      }
    }
  });

  // Handle initial page load with hash in URL
  if (window.location.hash) {
    const handleInitialHash = () => {
      const target = findAnchorTarget(window.location.hash);
      if (target) {
        setTimeout(() => {
          scrollToTarget(target);
        }, 150);
      }
    };

    if (document.readyState === 'complete') {
      handleInitialHash();
    } else {
      window.addEventListener('load', handleInitialHash, { once: true });
    }
  }

  // Handle hash changes (e.g. back/forward navigation)
  window.addEventListener('hashchange', () => {
    if (window.location.hash) {
      const target = findAnchorTarget(window.location.hash);
      if (target) {
        scrollToTarget(target);
      }
    }
  });
})();
